import { ChangeDetectorRef, Component, forwardRef, Input, OnDestroy } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR,
  UntypedFormControl, ValidationErrors, Validator
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Self-contained phone number field component with international support.
 * Includes mat-form-field, flag icon, and phone input in one component.
 *
 * Usage:
 * <app-phone-field formControlName="phoneNumber"
 *                  label="Phone number"
 *                  [campaignCountry]="'US'"></app-phone-field>
 */
@Component({
  selector: 'app-phone-field',
  templateUrl: './phone-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneFieldComponent),
      multi: true
    }
  ]
})
export class PhoneFieldComponent implements ControlValueAccessor, Validator, OnDestroy {
  @Input() label = 'Phone number';
  @Input() placeholder?: string;
  @Input() campaignCountry?: string = 'US';
  @Input() required?: boolean;
  @Input() hideRequiredMarker?: boolean;
  @Input() fieldClass?: string;
  @Input() countryFlagTooltip?: string;

  phoneControl = new UntypedFormControl();
  flagIcon = 'flag-icon-us';

  private _destroy$ = new Subject<void>();
  private _onChange = (_: any) => { };
  private _onTouched = () => { };

  constructor(private _cd: ChangeDetectorRef) {
    // Listen to phone control changes and propagate to parent form
    this.phoneControl.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(value => {
        this._onChange(value);
      });
  }

  get isFlagDisabled(): boolean {
    const value = this.phoneControl.value;
    return value === null || value === undefined || value === '';
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  updateFlagIcon(countryCode: string): void {
    this.flagIcon = 'flag-icon-' + countryCode.toLowerCase();
    this._cd.markForCheck();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.phoneControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.phoneControl.disable();
    } else {
      this.phoneControl.enable();
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    // Forward validation errors from inner phone control
    return this.phoneControl.invalid ? this.phoneControl.errors : null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.phoneControl.statusChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(fn);
  }
}
